import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import BackButton from '../components/ui/BackButton';

export const BuiltInMotorTechnologyPage = () => {
  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <BackButton to="/motorization/smart-connectivity" label="Smart Connectivity" />
      </div>

      {/* Hero Section - Title overlaid on motor image */}
      <section className="relative h-[45vh] lg:h-[50vh] overflow-hidden" data-testid="motor-tech-hero">
        <img 
          src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/2uphetbb_image.png"
          alt="Built-in motor technology"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="container-xl relative z-10 h-full flex flex-col justify-center items-center text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-white text-5xl lg:text-6xl font-serif leading-tight mb-4">
              Built-in technologies
            </h1>
            <p className="text-2xl lg:text-3xl text-white/80 font-light">
              Smart motors. Smarter choices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro Text */}
      <section className="py-12 bg-white">
        <div className="container-xl">
          <p className="text-neutral-600 text-base leading-relaxed max-w-5xl">
            At Motionblinds, we offer a wide range of battery and wired motors - each with its own built-in technology. 
            Whether your project needs advanced connectivity, simple setup, or professional integration, there's a 
            Motionblinds motor to match. From high-end residential homes to large-scale commercial buildings, our 
            tech-ready motors provide the flexibility you need. Want to compare technologies?{' '}
            <Link to="/blog" className="text-amber-700 hover:underline">
              Explore the differences
            </Link>{' '}
            between Bluetooth, Bridge, and Eve Motionblinds with Matter and Thread.
          </p>
        </div>
      </section>

      {/* Section 1: Matter and Thread - Image LEFT, Text RIGHT */}
      <section className="py-6 bg-white" data-testid="matter-thread-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg shadow-sm">
            {/* Image LEFT */}
            <div className="bg-[#d4c4b0] aspect-[4/3] lg:aspect-auto flex items-center justify-center p-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/2w04pizt_image.png"
                alt="Matter and Thread mesh network"
                className="w-full h-full object-contain max-h-[300px]"
              />
            </div>
            
            {/* Text RIGHT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center p-8 lg:p-12">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Matter and Thread</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Our battery and wired motors with built-in Matter and Thread connect effortlessly with major 
                smart home platforms and building management systems that support these technologies. Thread 
                enables secure, low-power mesh networking - ideal for battery-powered motors. Whilst wired 
                motors stay constantly powered, acting as Thread routers to strengthen the network. No 
                Motionblinds Bridge is required—just a Thread Border Router for full integration.
              </p>
              <div>
                <Link to="/motorization/smart-connectivity/matter">
                  <Button 
                    variant="outline" 
                    className="border-neutral-400 text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 rounded-full px-8 py-5 text-sm font-medium"
                  >
                    Read more
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Bluetooth - Text LEFT, Image RIGHT */}
      <section className="py-6 bg-white" data-testid="bluetooth-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg shadow-sm">
            {/* Text LEFT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center p-8 lg:p-12 order-2 lg:order-1">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Bluetooth</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Our Bluetooth motors feature built-in technology for quick, direct setup via the Motionblinds 
                Bluetooth app. No bridge or hub required. This entry-level smart solution is ideal for homes 
                and small-scale commercial projects where simplicity and speed matter.
              </p>
            </div>
            
            {/* Image RIGHT */}
            <div className="bg-[#e8e4e0] aspect-[4/3] lg:aspect-auto flex items-center justify-center p-8 order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/oocalpei_image.png"
                alt="Bluetooth app control - hand holding phone near blinds"
                className="w-full h-full object-contain max-h-[300px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: 433MHz Radio - Image LEFT, Text RIGHT */}
      <section className="py-6 bg-white" data-testid="radio-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg shadow-sm">
            {/* Image LEFT */}
            <div className="bg-[#f0ede9] aspect-[4/3] lg:aspect-auto flex items-center justify-center p-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/l6azfnsz_image.png"
                alt="433MHz bridge device"
                className="w-full h-full object-contain max-h-[300px]"
              />
            </div>
            
            {/* Text RIGHT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center p-8 lg:p-12">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">433MHz radio</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Motors with 433MHz radio technology support a variety of control options - from handheld 
                remotes and wall switches to Motionblinds Bridges and building automation systems. Bridges 
                connect easily to smart home platforms, while domotica boxes enable full integration into 
                commercial building management systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Dry Contacts - Text LEFT, Image RIGHT */}
      <section className="py-6 bg-white" data-testid="dry-contacts-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg shadow-sm">
            {/* Text LEFT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center p-8 lg:p-12 order-2 lg:order-1">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Dry contacts</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Wired motors with dry contacts allow easy integration with building management systems (BMS), 
                making them ideal for smart buildings. Dry contacts offer versatile control options to suit 
                various needs. This connectivity method works with a wide range of control systems, from 
                simple wall switches to advanced automation systems.
              </p>
            </div>
            
            {/* Image RIGHT */}
            <div className="bg-[#e5e0db] aspect-[4/3] lg:aspect-auto flex items-center justify-center p-8 order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/da486wo9_image.png"
                alt="Modern office with large windows and automated blinds"
                className="w-full h-full object-contain max-h-[300px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f5f5f5]" data-testid="cta-section">
        <div className="container-xl">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Where to buy Motionblinds?</h3>
              <p className="text-neutral-500 mb-6 text-sm">Find a retailer or dealer near you.</p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full px-6 text-sm">
                    Find your store
                  </Button>
                </Link>
                <Link to="/trade">
                  <Button variant="outline" className="border-neutral-400 text-neutral-900 hover:bg-neutral-900 hover:text-white rounded-full px-6 text-sm">
                    For professionals
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Support center | FAQ</h3>
              <p className="text-neutral-500 mb-6 text-sm">Get answers to common questions.</p>
              <Link to="/faq">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full px-6 text-sm">
                  Find your answer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BuiltInMotorTechnologyPage;
